export interface TierDefinition {
  readonly id: number;
  readonly name: string;
  readonly nameJa: string;
  readonly metersPerUnit: number;
  readonly sky: number;
  readonly horizon: number;
  readonly ground: number;
  readonly accent: number;
  readonly fogDensity: number;
  readonly itemNames: readonly string[];
}

export const ENTER_RADIUS_UNITS = 0.8;
export const EXIT_RADIUS_UNITS = 3.2;
export const TIER_SCALE_RATIO = 4;
export const INITIAL_RADIUS_METERS = ENTER_RADIUS_UNITS * 0.25;
export const PICKUP_RADIUS_RATIO = 0.72;
export const PICKUP_VOLUME_EFFICIENCY = 2.35;
export const GOAL_OBJECT_NAME = "TOKYO SKYTREE · 東京スカイツリー";
// The low-poly goal uses a 2:1 height-to-equivalent-radius model so 317 m
// renders at the real tower height of 634 m while keeping pickup balance
// independent from the visual silhouette.
export const GOAL_OBJECT_RADIUS_METERS = 317;
export const SKYTREE_VISUAL_HEIGHT_METERS = 634;
export const GOAL_REQUIRED_RADIUS_METERS = GOAL_OBJECT_RADIUS_METERS / PICKUP_RADIUS_RATIO;
export const SKYTREE_X_METERS = 2_560;
export const SKYTREE_Z_METERS = -1_280;
export const GOAL_SPAWN_DISTANCE_METERS = Math.hypot(SKYTREE_X_METERS, SKYTREE_Z_METERS);
export const TRANSITION_DURATION_SECONDS = 1.8;
export const FIXED_STEP_SECONDS = 1 / 60;
export const MAX_CATCH_UP_STEPS = 3;
export const WORLD_SEED = 0x50_4f_4c_41;
export const STREAM_RADIUS_CHUNKS = 2;
export const CHUNK_SIZE_UNITS = 15;
export const ITEMS_PER_CHUNK = 24;
export const ARCHETYPE_COUNT = 5;
export const VISIBLE_CHUNK_COUNT = (STREAM_RADIUS_CHUNKS * 2 + 1) ** 2;
export const MAX_WORLD_INSTANCES = VISIBLE_CHUNK_COUNT * ITEMS_PER_CHUNK;
export const MAX_ARCHETYPE_INSTANCES =
  VISIBLE_CHUNK_COUNT * Math.ceil(ITEMS_PER_CHUNK / ARCHETYPE_COUNT);
export const GPU_WORLD_INSTANCE_CAPACITY = MAX_ARCHETYPE_INSTANCES * ARCHETYPE_COUNT;
export const MAX_ATTACHED_INSTANCES = 60;
export const MAX_PARTICLES = 72;

export const TIERS: readonly TierDefinition[] = [
  {
    id: 0,
    name: "HOME ROOM",
    nameJa: "民家の居間",
    metersPerUnit: 0.25,
    sky: 0x7fc8ef,
    horizon: 0xffd8af,
    ground: 0xc99f68,
    accent: 0xff5c70,
    fogDensity: 0.014,
    itemNames: ["Button", "Glass Bead", "Tiny Screw", "Toy Block", "Coin"],
  },
  {
    id: 1,
    name: "HOME & GARDEN",
    nameJa: "庭と住宅街",
    metersPerUnit: 1,
    sky: 0x75c7f1,
    horizon: 0xffdbb4,
    ground: 0x83a95a,
    accent: 0xffcf45,
    fogDensity: 0.0135,
    itemNames: ["Tea Cup", "Floor Lamp", "Garden Bench", "Bonsai", "Mailbox"],
  },
  {
    id: 2,
    name: "TOKYO STREET",
    nameJa: "秋葉原通り",
    metersPerUnit: 4,
    sky: 0x69b9e9,
    horizon: 0xffcda4,
    ground: 0x777d86,
    accent: 0xff4f93,
    fogDensity: 0.013,
    itemNames: ["Traffic Cone", "Vending Machine", "Neon Kiosk", "Delivery Bike", "Utility Pole"],
  },
  {
    id: 3,
    name: "TOKYO DISTRICT",
    nameJa: "上野・浅草",
    metersPerUnit: 16,
    sky: 0x62b5e6,
    horizon: 0xffd0a9,
    ground: 0x6f7f86,
    accent: 0x30d5c8,
    fogDensity: 0.0125,
    itemNames: ["Convenience Store", "Footbridge", "Station Gate", "Temple Roof", "Office Sign"],
  },
  {
    id: 4,
    name: "TOKYO METROPOLIS",
    nameJa: "都心環状",
    metersPerUnit: 64,
    sky: 0x66b9e8,
    horizon: 0xffd6b7,
    ground: 0x718b83,
    accent: 0xff8c42,
    fogDensity: 0.012,
    itemNames: ["Office Tower", "Stadium", "Harbor Crane", "Broadcast Mast", "River Bridge"],
  },
  {
    id: 5,
    name: "SKYTREE SKYLINE",
    nameJa: "東京スカイライン",
    metersPerUnit: 256,
    sky: 0x77c8ee,
    horizon: 0xffe1c4,
    ground: 0x76918a,
    accent: 0x8a5cff,
    fogDensity: 0.0115,
    itemNames: ["Tokyo Ward", "Cloud Bank", "Expressway Loop", "City Cluster", "Bay Island"],
  },
] as const;

export const FINAL_RADIUS_METERS =
  (TIERS.at(-1)?.metersPerUnit ?? 1) * EXIT_RADIUS_UNITS;

export function getTier(index: number): TierDefinition {
  const bounded = Math.max(0, Math.min(TIERS.length - 1, Math.floor(index)));
  const tier = TIERS[bounded];
  if (!tier) {
    throw new Error(`Missing tier ${bounded}`);
  }
  return tier;
}

export function tierIndexForRadius(radiusMeters: number): number {
  for (let index = TIERS.length - 1; index >= 0; index -= 1) {
    const tier = TIERS[index];
    if (tier && radiusMeters >= tier.metersPerUnit * ENTER_RADIUS_UNITS) {
      return index;
    }
  }
  return 0;
}

export function tierProgress(radiusMeters: number, tierIndex: number): number {
  const tier = getTier(tierIndex);
  const localRadius = radiusMeters / tier.metersPerUnit;
  return Math.max(
    0,
    Math.min(1, (localRadius - ENTER_RADIUS_UNITS) / (EXIT_RADIUS_UNITS - ENTER_RADIUS_UNITS)),
  );
}
